<template>
  <v-card class="mb-4 rounded-lg" outlined>
    <v-card-title class="pb-2">{{ $t("online_store_media") }}</v-card-title>
    <v-card-text>
      <div class="text-caption grey--text mb-3">
        {{ $t("online_store_media_hint") }}
      </div>

      <div class="text-caption grey--text mb-2">{{ $t("product_gallery") }}</div>
      <v-row v-if="galleryItems.length" dense class="mb-3">
        <v-col
          v-for="item in galleryItems"
          :key="item.key"
          cols="6"
          sm="4"
          md="3"
        >
          <v-card outlined rounded="lg" class="product-gallery-thumb">
            <v-img :src="item.src" height="96" contain class="grey lighten-4">
              <template v-slot:placeholder>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-progress-circular indeterminate size="24" color="primary" />
                </v-row>
              </template>
            </v-img>
            <v-btn
              icon
              x-small
              color="error"
              class="product-gallery-thumb__remove"
              :aria-label="$t('delete')"
              @click="removeGalleryItem(item)"
            >
              <v-icon x-small>fal fa-times</v-icon>
            </v-btn>
          </v-card>
        </v-col>
      </v-row>

      <div v-if="canAddMoreImages" class="mb-4">
        <DragDropUpload
          :value="null"
          accept="image/*"
          :max-size-mb="5"
          multiple
          @files-selected="onGalleryFilesSelected"
        />
      </div>
      <div v-else class="text-caption grey--text mb-4">
        {{ $t("gallery_max_reached") }}
      </div>

      <TTextField
        skip-translation
        :label="$t('video_review_url')"
        v-model="localVideoUrl"
        :errorMessages="videoError ? [videoError] : []"
        @input="emitVideoUrl"
        @blur="validateVideoUrl"
      />
      <div class="text-caption grey--text mt-1 mb-3">
        {{ $t("video_review_hint") }}
      </div>

      <v-switch
        v-model="localIsOnline"
        :label="$t('is_online')"
        inset
        hide-details
        class="mt-0"
        @change="emitIsOnline"
      />
    </v-card-text>
  </v-card>
</template>

<script>
import DragDropUpload from "@/components/global/DragDropUpload.vue";
import TTextField from "@/components/global/TextField";

const MAX_GALLERY = 4;
const VIDEO_URL_PATTERN =
  /^https?:\/\/(www\.)?(youtube\.com\/watch\?v=[\w-]+(?:&[\w=%.-]*)?|youtu\.be\/[\w-]+(?:\?[\w=%.&-]*)?|youtube\.com\/embed\/[\w-]+|vimeo\.com\/\d+)/i;

function resolveProductImageUrl(val) {
  if (!val) {
    return "";
  }
  if (/^https?:\/\//i.test(val)) {
    return val;
  }
  const base = process.env.VUE_APP_BASE_URL || "";
  return `${base}/uploads/${val}`;
}

export default {
  name: "ProductOnlineMedia",

  components: {
    DragDropUpload,
    TTextField,
  },

  props: {
    existingImages: {
      type: Array,
      default: () => [],
    },
    pendingFiles: {
      type: Array,
      default: () => [],
    },
    videoUrl: {
      type: String,
      default: "",
    },
    isOnline: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      localVideoUrl: this.videoUrl || "",
      localIsOnline: this.isOnline,
      videoError: "",
      pendingPreviews: [],
      previewRevokeUrls: [],
    };
  },

  computed: {
    galleryItems() {
      const items = [];

      (this.existingImages || []).forEach((row) => {
        items.push({
          key: `existing-${row.id}`,
          kind: "existing",
          id: row.id,
          src: resolveProductImageUrl(row.image),
        });
      });

      this.pendingPreviews.forEach((preview) => {
        items.push({
          key: preview.key,
          kind: "pending",
          src: preview.src,
          fileKey: preview.key,
        });
      });

      return items;
    },

    canAddMoreImages() {
      return this.galleryItems.length < MAX_GALLERY;
    },
  },

  watch: {
    videoUrl(value) {
      this.localVideoUrl = value || "";
    },
    isOnline(value) {
      this.localIsOnline = !!value;
    },
    pendingFiles: {
      handler(files) {
        this.syncPendingPreviews(files || []);
      },
      deep: true,
    },
  },

  beforeDestroy() {
    this.revokePreviewUrls();
  },

  methods: {
    syncPendingPreviews(files) {
      this.revokePreviewUrls();
      this.pendingPreviews = (files || []).map((file, index) => {
        const key = `pending-${index}-${file.name}-${file.size}`;
        const src = URL.createObjectURL(file);
        this.previewRevokeUrls.push(src);
        return { key, src, file };
      });
    },

    revokePreviewUrls() {
      this.previewRevokeUrls.forEach((src) => {
        try {
          URL.revokeObjectURL(src);
        } catch (e) {
          /* ignore */
        }
      });
      this.previewRevokeUrls = [];
      this.pendingPreviews = [];
    },

    onGalleryFilesSelected(files) {
      if (!Array.isArray(files) || !files.length || !this.canAddMoreImages) {
        return;
      }

      const slotsLeft = MAX_GALLERY - this.galleryItems.length;
      if (slotsLeft <= 0) {
        return;
      }

      const accepted = files.slice(0, slotsLeft);
      const next = [...(this.pendingFiles || []), ...accepted];
      this.$emit("update:pendingFiles", next);
    },

    removeGalleryItem(item) {
      if (item.kind === "existing") {
        const next = (this.existingImages || []).filter(
          (row) => row.id !== item.id
        );
        this.$emit("update:existingImages", next);
        return;
      }

      const index = this.pendingPreviews.findIndex(
        (preview) => preview.key === item.key
      );
      if (index === -1) {
        return;
      }

      const nextFiles = [...(this.pendingFiles || [])];
      nextFiles.splice(index, 1);
      this.$emit("update:pendingFiles", nextFiles);
    },

    validateVideoUrl() {
      const value = (this.localVideoUrl || "").trim();
      if (!value) {
        this.videoError = "";
        return true;
      }

      if (!VIDEO_URL_PATTERN.test(value)) {
        this.videoError = this.$t("invalid_video_url");
        return false;
      }

      this.videoError = "";
      return true;
    },

    emitVideoUrl() {
      this.validateVideoUrl();
      this.$emit("update:videoUrl", this.localVideoUrl);
    },

    emitIsOnline() {
      this.$emit("update:isOnline", this.localIsOnline);
    },

    validate() {
      return this.validateVideoUrl();
    },

    getGalleryKeepIds() {
      return (this.existingImages || []).map((row) => row.id);
    },
  },
};
</script>

<style scoped>
.product-gallery-thumb {
  position: relative;
}

.product-gallery-thumb__remove {
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(255, 255, 255, 0.92) !important;
}
</style>
